import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShoppingCart, Search } from 'lucide-react';
import { IconButton } from '../common/TouchButton';
import { useKiosk } from '../../context/KioskContext';
import { formatCurrency } from '../../utils/formatCurrency';

/**
 * KioskHeader - Persistent header component for kiosk screens
 * 
 * Features:
 * - Back button (optional)
 * - Restaurant logo/title
 * - Search button (optional)
 * - Cart button with count and subtotal
 */
export default function KioskHeader({
  showBack = false,
  showSearch = false,
  showCart = true,
  title,
  subtitle,
  onBackClick,
  onSearchClick,
  onCartClick,
  className = '',
}) {
  const navigate = useNavigate();
  const { config, cart, getCartSubtotal } = useKiosk();

  const cartCount = cart.reduce((sum, item) => sum + (item.quantity || 1), 0);
  const cartSubtotal = getCartSubtotal();

  const handleBack = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const handleCartClick = () => {
    if (onCartClick) {
      onCartClick();
    } else {
      navigate('cart');
    }
  };

  const restaurantName = title || config?.restaurant?.name || 'Chain29';
  const restaurantLogo = config?.restaurant?.logo_url;

  return (
    <header 
      className={`
        sticky top-0 z-50
        h-20 px-6
        flex items-center justify-between
        bg-[#0f1419]/95 backdrop-blur-sm
        border-b border-white/10
        ${className}
      `}
      role="banner"
    >
      {/* Left Section - Back Button */}
      <div className="flex items-center gap-4 min-w-[120px]">
        {showBack && (
          <IconButton
            icon={ArrowLeft}
            onClick={handleBack}
            aria-label="Go back"
          />
        )}
      </div>

      {/* Center Section - Logo/Title */}
      <div className="flex items-center gap-3">
        {restaurantLogo && (
          <img 
            src={restaurantLogo} 
            alt={restaurantName}
            className="h-10 w-10 rounded-lg object-cover"
          />
        )}
        <div className="text-center">
          <h1 className="text-xl font-semibold text-white font-['Outfit']">
            {restaurantName}
          </h1>
          {subtitle && (
            <p className="text-sm text-gray-400">{subtitle}</p>
          )}
        </div>
      </div>

      {/* Right Section - Search & Cart */}
      <div className="flex items-center gap-3 min-w-[120px] justify-end">
        {showSearch && (
          <IconButton
            icon={Search}
            onClick={onSearchClick}
            aria-label="Search menu"
          />
        )}

        {showCart && (
          <button
            onClick={handleCartClick}
            disabled={cartCount === 0}
            className={`
              flex items-center gap-3 h-12 px-4 
              rounded-xl transition-all duration-150
              ${cartCount > 0 
                ? 'bg-[#f5a623] text-[#0f1419] hover:brightness-110' 
                : 'bg-[#252b33] text-gray-500 cursor-not-allowed'
              }
            `}
            aria-label={`View cart with ${cartCount} items`}
          >
            <div className="relative">
              <ShoppingCart size={22} />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-[#0f1419] text-[#f5a623] text-xs font-bold rounded-full">
                  {cartCount > 9 ? '9+' : cartCount}
                </span>
              )}
            </div>
            {cartCount > 0 && (
              <span className="font-semibold text-base">
                {formatCurrency(cartSubtotal)}
              </span>
            )}
          </button>
        )}
      </div>
    </header>
  );
}
